import { Pin, PinOff } from "lucide-react";
import { useEffect, useState } from "react";
import { cachePageAssets, pinnedPages, setPinned } from "../offline/pinning";
import type { DocHandle } from "../sync/docManager";
import { useT } from "../i18n";

/** "Available offline" toggle (spec §8.13): pins the doc and caches its media in OPFS. */
export function PinToggle({ handle }: { handle: DocHandle }) {
  const t = useT();
  const [pinned, setPin] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  useEffect(() => {
    void pinnedPages().then((s) => setPin(s.has(handle.crdtDocId)));
  }, [handle.crdtDocId]);
  const toggle = async () => {
    const next = !pinned;
    setPin(next);
    // mark caching before any await: until the media is in OPFS the page is not yet available
    // offline, and the button must not claim otherwise
    if (next) setProgress(t("offline.caching"));
    await setPinned(handle.crdtDocId, next);
    if (next) {
      try {
        await cachePageAssets(handle.doc, (done, total) => setProgress(total ? t("offline.cachingProgress", { done, total }) : null));
      } finally {
        setProgress(null);
      }
    }
  };
  return (
    <button className="flex items-center gap-1 rounded px-2 py-0.5 text-xs hover:bg-neutral-200 dark:hover:bg-neutral-800" onClick={() => void toggle()} title={t("offline.title")} data-testid="pin-toggle" data-pinned={pinned} data-caching={progress !== null}>
      {pinned ? <Pin size={14} /> : <PinOff size={14} />}
      {progress ?? (pinned ? t("offline.pinned") : t("offline.pin"))}
    </button>
  );
}
